/**
 * The client-side request deadline (ZOY-466).
 *
 * A request that FAILS settles; a request that STALLS never does — no
 * rejection, so no retry, no backoff ceiling, and every surface waiting on it
 * keeps rendering its skeleton or spinner forever. Nothing below this client
 * bounds that: the backend has no response deadline and the Caddy edge
 * proxies with unlimited defaults, and neither would help anyway when the
 * stall is in the network path itself (a captive portal, a proxy holding the
 * socket). The deadline has to live here.
 *
 * 12s matches the in-house precedent already running against this same
 * backend (adminpanel/src/lib/clinicbackend.ts).
 *
 * The one subtlety this exists to get right: OUR abort and the CALLER's abort
 * arrive identically, as a bare `AbortError` DOMException. Conflating them
 * would either turn a deliberate cancellation into a fake network failure, or
 * let a timeout escape as a raw DOMException that bypasses every surface's
 * error branch — reproducing the very bug this fixes, in a new costume. So
 * the timer sets a flag the caller can ask about, and forwarding is one-way:
 * a caller's abort cancels the request without ever being read as a timeout.
 */

export const REQUEST_TIMEOUT_MS = 12_000;

export interface Deadline {
  /** Pass to fetch/XHR in place of the caller's signal. */
  signal: AbortSignal;
  /** True only when OUR timer fired — never for a caller-initiated abort. */
  timedOut: () => boolean;
  /** Always call in `finally`: clears the timer and unsubscribes. */
  done: () => void;
}

export function withDeadline(
  caller?: AbortSignal | null,
  ms: number = REQUEST_TIMEOUT_MS,
): Deadline {
  const controller = new AbortController();
  let timedOut = false;

  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, ms);

  // One-way forwarding: the caller can cancel us, but their abort never sets
  // the timeout flag, so it stays distinguishable all the way up.
  const onCallerAbort = () => controller.abort();
  if (caller) {
    if (caller.aborted) controller.abort();
    else caller.addEventListener('abort', onCallerAbort, { once: true });
  }

  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    done: () => {
      clearTimeout(timer);
      caller?.removeEventListener('abort', onCallerAbort);
    },
  };
}

/** A bare abort from fetch/XHR, whoever caused it. */
export function isAbortError(e: unknown): boolean {
  return e instanceof DOMException && e.name === 'AbortError';
}
